import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import masterActions from "../../redux/master/actions";
import commonActions from "../../redux/common/actions";
import { FORM_ASYNC_API_URL } from "../../utils/constants";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";
import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";

const STATUS_COLORS = {
  COMPLETED: "success",
  PROCESSING: "warning",
  FAILED: "error",
  PENDING: "default",
};

export default function GeneratePDF() {
  const dispatch = useDispatch();
  const { courseList } = useSelector((state) => state.masterReducer);

  const [courseid, setCourseId] = useState(0);
  const [pcmb, setPcmb] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfStatus, setPdfStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const pollingRef = useRef(null);

  useEffect(() => {
    dispatch({ type: masterActions.GET_COURSE_LIST });
  }, [dispatch]);

  const getToken = () => Cookies.get("mhet_mst_token");

  const fetchStatus = async (id) => {
    try {
      const { data } = await axios.get(
        `${FORM_ASYNC_API_URL}/getPDFStatus/${id}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setPdfStatus(data);
    } catch {
      setPdfStatus(null);
    }
  };

  // Poll every 5s when a course is selected
  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setPdfStatus(null);

    if (courseid) {
      setStatusLoading(true);
      fetchStatus(courseid).finally(() => setStatusLoading(false));
      pollingRef.current = setInterval(() => fetchStatus(courseid), 5000);
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [courseid]);

  const selectedCourse = courseList?.all?.find((c) => c.courseid === courseid);
  const showSubjectGroup = !!selectedCourse?.ispcmb;
  const showAttempt = [124, 125].includes(courseid);

  const handleCourseChange = (e) => {
    setCourseId(e.target.value);
    setPcmb(0);
    setAttempt(0);
  };

  const isActionDisabled =
    !courseid ||
    (showSubjectGroup && !pcmb) ||
    (showAttempt && !attempt);

  const showAlert = (status, message) => {
    dispatch({ type: commonActions.SET_ALERT, payload: { status, show: true, message } });
    setTimeout(() => {
      dispatch({ type: commonActions.SET_ALERT, payload: { status, show: false, message: null } });
    }, 3000);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = { courseid };
      if (showSubjectGroup) payload.pcmb = pcmb;
      if (showAttempt) payload.attempt = attempt;

      await axios.post(`${FORM_ASYNC_API_URL}/generatePDF/${courseid}`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      showAlert("success", "PDF generation started successfully");
      // Immediately refresh status
      await fetchStatus(courseid);
    } catch (err) {
      showAlert("failed", err?.response?.data?.message || "Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.get(
        `${FORM_ASYNC_API_URL}/getPDFSignedUrl/${courseid}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (data?.signedUrl) {
        const link = document.createElement("a");
        link.href = data.signedUrl;
        link.setAttribute("download", `pdf_dump_${courseid}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        showAlert("failed", "No signed URL received");
      }
    } catch (err) {
      showAlert("failed", err?.response?.data?.message || "Failed to get download URL");
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <Box>
      {/* Selectors + Actions */}
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <FormControl sx={{ minWidth: 220 }}>
            <Select size="small" value={courseid} onChange={handleCourseChange}>
              <MenuItem value={0}>Select Course</MenuItem>
              {courseList?.all?.map((course, i) => (
                <MenuItem key={i} value={course.courseid}>
                  {course.coursename}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {showSubjectGroup && (
          <Grid item>
            <FormControl sx={{ minWidth: 180 }}>
              <Select size="small" value={pcmb} onChange={(e) => setPcmb(e.target.value)}>
                <MenuItem value={0}>Select Subject Group</MenuItem>
                <MenuItem value={1}>PCM</MenuItem>
                <MenuItem value={2}>PCB</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {showAttempt && (
          <Grid item>
            <FormControl sx={{ minWidth: 160 }}>
              <Select size="small" value={attempt} onChange={(e) => setAttempt(e.target.value)}>
                <MenuItem value={0}>Select Attempt</MenuItem>
                <MenuItem value={1}>Attempt 1</MenuItem>
                <MenuItem value={2}>Attempt 2</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item>
          <BluePrimaryButton
            title={generating ? "Generating..." : "Generate PDF"}
            handleSubmit={handleGenerate}
            disabled={isActionDisabled || generating || downloading}
          />
        </Grid>

        {pdfStatus?.status === "COMPLETED" && (
          <Grid item>
            <MauvelousPrimaryButton
              title={downloading ? "Downloading..." : "Download PDF"}
              handleClick={handleDownload}
              disabled={downloading || generating}
            />
          </Grid>
        )}

        {(generating || downloading || statusLoading) && (
          <Grid item>
            <CircularProgress size={24} />
          </Grid>
        )}
      </Grid>

      {/* Status Card */}
      {courseid > 0 && (
        <Box
          mt={3}
          p={2.5}
          sx={{ bgcolor: "#f5f5f5", borderRadius: 2, maxWidth: 520 }}
        >
          <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
            PDF Generation Status
          </Typography>

          {statusLoading && !pdfStatus ? (
            <CircularProgress size={20} />
          ) : pdfStatus ? (
            <Grid container spacing={1}>
              <Grid item xs={5}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
              </Grid>
              <Grid item xs={7}>
                <Chip
                  label={pdfStatus.status}
                  color={STATUS_COLORS[pdfStatus.status] || "default"}
                  size="small"
                />
              </Grid>

              <Grid item xs={5}>
                <Typography variant="body2" color="text.secondary">Started At</Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2">{formatDate(pdfStatus.startedAt)}</Typography>
              </Grid>

              <Grid item xs={5}>
                <Typography variant="body2" color="text.secondary">Last Updated</Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2">{formatDate(pdfStatus.updatedAt)}</Typography>
              </Grid>

              {pdfStatus.errormessage && (
                <>
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">Error</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2" color="error.main">
                      {pdfStatus.errormessage}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No status available for this course.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
